// ЕЖВ.1: виджет «Ежедневный вход» на Home.
//
// Состояния:
// - claimed_today=false → пульсирует opacity, текст «Забрать +N Скр».
//   Клик: claim → анимация бейджа → state меняется. Затем переход
//   на /daily-login.
// - claimed_today=true  → серая рамка без пульсации, текст «Забрано ·
//   следующая в 00:00 UTC». Клик → /daily-login.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '../telegram/useTelegram';
import { useGameStore } from '../store/useGameStore';
import { getDailyLoginStatus, claimDailyLogin } from '../api/client';
import { ScriptsIcon } from './ScriptsIcon';

interface Props {
  className?: string;
}

export function DailyLoginWidget({ className }: Props) {
  const nav = useNavigate();
  const { hapticTap, hapticSuccess, hapticError } = useTelegram();
  const dailyLogin = useGameStore(s => s.dailyLogin);
  const setDailyLogin = useGameStore(s => s.setDailyLogin);
  const addScripts = useGameStore(s => s.addScripts);
  const [busy, setBusy] = useState(false);
  const [popReward, setPopReward] = useState<number | null>(null);

  // Подтягиваем при маунте если ещё не загружено.
  useEffect(() => {
    if (dailyLogin) return;
    (async () => {
      try {
        const s = await getDailyLoginStatus();
        setDailyLogin(s);
      } catch { /* offline */ }
    })();
  }, [dailyLogin, setDailyLogin]);

  const onClick = async () => {
    hapticTap();
    // Если ещё не забрано — забираем + анимация. Затем (после задержки)
    // переход на /daily-login.
    if (dailyLogin && !dailyLogin.claimed_today) {
      setBusy(true);
      try {
        const r = await claimDailyLogin();
        hapticSuccess();
        setPopReward(r.reward);
        addScripts(r.reward);
        // Обновляем статус локально (на сервере уже claimed_today=true).
        const fresh = await getDailyLoginStatus();
        setDailyLogin(fresh);
        // После короткой анимации — перейти на экран.
        setTimeout(() => {
          setPopReward(null);
          nav('/daily-login');
        }, 1100);
      } catch {
        hapticError();
        nav('/daily-login');
      } finally {
        setBusy(false);
      }
      return;
    }
    nav('/daily-login');
  };

  // Сколько Скриптов покажется как «следующая награда».
  const nextDay = (() => {
    if (!dailyLogin) return null;
    if (dailyLogin.claimed_today) {
      // current_day в стейте уже «следующий день» (после claim увеличился).
      // Используем именно его.
      return dailyLogin.current_day;
    }
    return dailyLogin.current_day;
  })();
  const nextReward = nextDay ? dailyLogin?.rewards?.[nextDay - 1] : null;
  const claimed = !!dailyLogin?.claimed_today;

  return (
    <button
      onClick={onClick}
      disabled={busy}
      className={className}
      data-tt="hm-daily"
      style={{
        position: 'relative',
        width: '100%',
        padding: '12px 14px',
        background: claimed
          ? 'rgba(255,255,255,0.04)'
          : 'rgba(156,255,61,0.08)',
        border: `0.5px solid ${claimed ? 'rgba(160,170,200,0.18)' : 'var(--neon-li)'}`,
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: 'inherit',
        animation: !claimed && dailyLogin ? 'dl-pulse 2s ease-in-out infinite' : undefined,
        opacity: busy ? 0.6 : 1
      }}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <div style={{
            fontSize: 10, color: 'var(--t-md)',
            letterSpacing: 1, marginBottom: 4
          }}>
            ЕЖЕДНЕВНЫЙ ВХОД
          </div>
          <div style={{
            fontSize: 14,
            color: claimed ? 'var(--t-md)' : 'var(--neon-li)',
            fontWeight: 600
          }}>
            {!dailyLogin
              ? 'Загрузка…'
              : claimed
                ? 'Забрано · следующая в 00:00 UTC'
                : <>Забрать +{nextReward} <ScriptsIcon size={12} /></>}
          </div>
        </div>
        <div style={{
          fontSize: 11, color: 'var(--t-lo)',
          textAlign: 'right', minWidth: 60
        }}>
          {dailyLogin && (
            <>
              серия<br />
              <strong style={{ color: 'var(--neon-am)', fontSize: 16 }}>
                {dailyLogin.current_streak_length}
              </strong>
            </>
          )}
        </div>
      </div>

      {/* Прилетающий бейдж +N Скр после клейма. */}
      {popReward !== null && (
        <div style={{
          position: 'absolute', top: '50%', right: 14,
          transform: 'translateY(-50%)',
          padding: '6px 12px',
          background: 'var(--neon-li)', color: 'var(--bg, #07070B)',
          fontWeight: 700, fontSize: 14, letterSpacing: 1,
          animation: 'dl-pop 1s ease-out forwards'
        }}>
          +{popReward} ◆
        </div>
      )}
    </button>
  );
}

// Глобальные keyframes (инжектим один раз).
if (typeof document !== 'undefined' && !document.getElementById('dl-keyframes')) {
  const s = document.createElement('style');
  s.id = 'dl-keyframes';
  s.textContent = `
    @keyframes dl-pulse {
      0%, 100% { opacity: 1; }
      50%      { opacity: 0.65; }
    }
    @keyframes dl-pop {
      0%   { transform: translateY(-50%) scale(0.6); opacity: 0; }
      30%  { transform: translateY(-50%) scale(1.1); opacity: 1; }
      80%  { transform: translateY(-150%) scale(1); opacity: 1; }
      100% { transform: translateY(-200%) scale(0.9); opacity: 0; }
    }
  `;
  document.head.appendChild(s);
}
